import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormControl } from '@angular/forms';

import { ReplaySubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { UserpilotService } from '@app/src/app/shared/services/userpilot';

@Component({
  selector: 'mg-form-toggle',
  templateUrl: './form-toggle.component.html',
  styleUrls: ['./form-toggle.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormToggleComponent implements OnChanges, OnInit, OnDestroy {
  /** Misc Subjects */
  private _destroyedSubject = new ReplaySubject<void>(1);

  /** Form Control */
  public formControl = this._fb.control(false);

  /** Inputs */
  @Input() label: string;
  @Input() secondaryLabel: string;
  @Input() tooltip: string;
  @Input() disabled: boolean;

  /**
   * Unique id for things like analytics and testing to hook into
   * Important to note changing this could break either of those
   */
  @Input() id: string;

  @Input()
  set control(formControl: UntypedFormControl | AbstractControl) {
    this.formControl = formControl as UntypedFormControl;
    this._cdr.markForCheck();
  }

  @Input() set value(val: boolean) {
    this.formControl.reset(val, { emitEvent: false });
  }

  @Output() valueChange = new EventEmitter<boolean>();

  get toggleDisabled() {
    return this.formControl.disabled;
  }

  /** Component Constructor */
  constructor(private _cdr: ChangeDetectorRef, private _fb: UntypedFormBuilder, private _userpilot: UserpilotService) {}

  /**
   * handles form control disabled state changes after Angular 16 migration
   * related to changes in Angular's input binding timing
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['disabled']) {
      if (changes['disabled'].currentValue) {
        this.formControl.disable();
      } else {
        this.formControl.enable();
      }
    }
  }

  ngOnInit(): void {
    this.formControl.valueChanges.pipe(takeUntil(this._destroyedSubject)).subscribe(val => {
      this._userpilot.track(this.id);
      this.formControl.markAsDirty();
      return this.valueChange.emit(val);
    });
  }

  ngOnDestroy(): void {
    this._destroyedSubject.next();
    this._destroyedSubject.complete();
  }
}
